/**
 * PhotoThumbnail - Centralized photo thumbnail component
 * 
 * ALWAYS use this for displaying photos in grids/lists.
 * Handles:
 * - Thumbnail loading with placeholder
 * - Error states (never shows broken image icon)
 * - Click to open lightbox
 * - Visibility badge
 * - Edit/Delete overlays
 */

import { useState, useRef } from "react";
import { Badge } from "../ui/badge";
import { Eye, EyeSlash, PencilSimple, Trash, Image as ImageIcon } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Build proper URL - handle both absolute and relative URLs
const buildUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const baseUrl = API || '';
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

export function PhotoThumbnail({
  photo,
  onClick,
  onEdit,
  onDelete,
  showVisibilityBadge = true,
  showActions = true,
  className = "",
}) {
  const [loaded, setLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  const thumbnailSrc = buildUrl(photo?.thumbnail_url || photo?.file_url);

  const handleClick = (e) => {
    e.stopPropagation();
    onClick?.(photo);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(photo);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(photo);
  };

  return (
    <div className={`relative group ${className}`}>
      <div 
        className="aspect-square rounded-sm border border-zinc-200 overflow-hidden cursor-pointer"
        onClick={handleClick}
      >
        {/* Loading placeholder */}
        {!loaded && !hasError && (
          <div className="absolute inset-0 bg-zinc-200 animate-pulse flex items-center justify-center">
            <ImageIcon size={24} className="text-zinc-400" />
          </div>
        )}
        
        {/* Error placeholder - styled, not broken icon */}
        {hasError && (
          <div className="absolute inset-0 bg-zinc-100 flex items-center justify-center">
            <ImageIcon size={24} className="text-zinc-300" />
          </div>
        )}
        
        {/* Actual thumbnail */}
        <img
          ref={imgRef}
          src={thumbnailSrc}
          alt={photo?.caption || "Photo"}
          onLoad={() => setLoaded(true)}
          onError={() => setHasError(true)}
          className={`w-full h-full object-cover transition-opacity ${
            loaded && !hasError ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </div>

      {/* Visibility badge */}
      {showVisibilityBadge && photo?.visibility && (
        <div className="absolute top-1 left-1">
          <Badge
            variant={photo.visibility === "customer" ? "default" : "secondary"}
            className={`text-xs ${photo.visibility === "customer" ? "bg-emerald-500" : "bg-zinc-500"}`}
          >
            {photo.visibility === "customer" ? <Eye size={10} /> : <EyeSlash size={10} />}
          </Badge>
        </div>
      )}

      {/* Actions overlay - only on hover */}
      {showActions && (onEdit || onDelete) && (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-sm">
          {onEdit && (
            <button
              onClick={handleEdit}
              className="p-2 bg-white rounded-full text-zinc-700 hover:bg-zinc-100"
              data-testid="photo-edit-btn"
            >
              <PencilSimple size={16} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={handleDelete}
              className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"
              data-testid="photo-delete-btn"
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      )}

      {/* Caption */}
      {photo?.caption && (
        <p className="mt-1 text-xs text-zinc-500 truncate">{photo.caption}</p>
      )}
    </div>
  );
}

export default PhotoThumbnail;
